import React, { useState, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { Seo } from '../components/Seo';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import { LifeBuoy, Search, Plane, Hotel, CreditCard, User, Ticket, Mail, Paperclip, Loader2, CheckCircle2, X } from 'lucide-react';

const CATEGORIES = [
  { key: 'flights', label: 'Flights', icon: Plane },
  { key: 'hotels', label: 'Hotels', icon: Hotel },
  { key: 'experiences', label: 'Experiences & Tours', icon: Ticket },
  { key: 'payments', label: 'Payments & booking', icon: CreditCard },
  { key: 'account', label: 'Account', icon: User },
];

const FAQS = [
  { cat: 'flights', q: 'How do I find the cheapest flight dates?', a: 'Open the departure date picker — we show the cheapest fare under each day and highlight the lowest in green. Flip months to compare, then pick your date.' },
  { cat: 'flights', q: 'Do you sell the tickets directly?', a: 'No. FlightDeals.in compares live fares from hundreds of airlines and agents. When you tap Book, we hand you off to the airline/agent to complete payment — we never add booking fees.' },
  { cat: 'flights', q: 'A fare changed when I clicked Book. Why?', a: 'Airfares are live and can change between search and checkout as seats sell. Always confirm the final price on the partner page before paying.' },
  { cat: 'flights', q: 'Can I search a whole city with multiple airports?', a: 'Yes — type the city (e.g. Goa) and every airport (GOI, GOX) appears so you can pick the exact one you want.' },
  { cat: 'hotels', q: 'Are the hotel prices live?', a: 'Yes. Prices and availability are fetched live for your dates and shown in your selected currency. The final price is confirmed with the booking partner before you pay.' },
  { cat: 'hotels', q: 'Who handles my hotel booking and cancellation?', a: 'Your booking, payment and any cancellation/refund are handled directly by our trusted hotel partner. We simply redirect you there to complete the reservation.' },
  { cat: 'experiences', q: 'What is the difference between Experiences and Tours?', a: 'Experiences are activities you can do in a day. Tours are multi-day trips (2+ days). Both are live inventory and you book securely on the partner site.' },
  { cat: 'payments', q: 'Does FlightDeals.in charge any fees?', a: 'No. We are a comparison service. You pay the airline, hotel or activity partner directly, with no added fees from us.' },
  { cat: 'payments', q: 'Is my payment secure?', a: 'Payments happen on the partner’s own secure checkout — we never see or store your card details.' },
  { cat: 'account', q: 'Do I need an account to search?', a: 'No, searching is free and open. An account lets you save searches and favourites across devices.' },
  { cat: 'account', q: 'How do I change my currency?', a: 'Use the currency switcher in the top bar — prices across flights, hotels, experiences and tours update instantly.' },
];

export default function Help() {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const [form, setForm] = useState({ name: '', email: '', category: 'flights', subject: '', message: '' });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return FAQS.filter((f) => (cat === 'all' || f.cat === cat)
      && (!ql || f.q.toLowerCase().includes(ql) || f.a.toLowerCase().includes(ql)));
  }, [q, cat]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) { toast.error('Screenshot too large (max 6MB).'); return; }
    setFile(f);
  };

  const submit = async () => {
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error('Please fill in your name, email, subject and message.'); return;
    }
    setSubmitting(true);
    try {
      let screenshot = null;
      if (file) {
        screenshot = await new Promise((res, rej) => {
          const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
        });
      }
      const { data } = await api.contact({ ...form, screenshot, screenshot_name: file?.name });
      setDone(true);
      toast.success(data.email_sent ? 'Message sent — we’ll get back to you soon!' : 'Message received — we’ll get back to you soon!');
      setForm({ name: '', email: '', category: form.category, subject: '', message: '' });
      setFile(null);
    } catch (e) {
      toast.error('Could not send your message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <Seo title="Help & Support — FlightDeals.in" description="Get help with flights, hotels, experiences and bookings on FlightDeals.in, or contact our team." />
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
          <div className="flex items-center gap-2 mb-2"><LifeBuoy className="h-6 w-6 text-accent" /><span className="text-[11px] uppercase tracking-wide text-primary-foreground/70">Help centre</span></div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">How can we help?</h1>
          <p className="text-primary-foreground/80 mt-2 text-base">Search our FAQs or reach the team directly — we usually reply within a day.</p>
          <div className="relative mt-5 max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search help articles…" data-testid="help-search"
              className="h-13 pl-12 h-12 bg-card text-foreground rounded-xl" />
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-5 gap-8">
        {/* FAQ */}
        <div className="lg:col-span-3">
          <div className="flex flex-wrap gap-2 mb-4" data-testid="help-categories">
            {[{ key: 'all', label: 'All', icon: LifeBuoy }, ...CATEGORIES].map((c) => (
              <button key={c.key} onClick={() => setCat(c.key)} data-testid={`help-cat-${c.key}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${cat === c.key ? 'bg-accent/12 text-accent border-accent/30' : 'text-muted-foreground border-border hover:bg-secondary'}`}>
                <c.icon className="h-3.5 w-3.5" /> {c.label}
              </button>
            ))}
          </div>
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8">No articles match “{q}”. Try the contact form →</p>
          ) : (
            <Accordion type="single" collapsible className="space-y-2" data-testid="help-faqs">
              {filtered.map((f, i) => (
                <AccordionItem key={i} value={`f${i}`} className="border border-border rounded-xl px-4">
                  <AccordionTrigger className="text-left text-sm font-medium">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>

        {/* Contact form */}
        <div className="lg:col-span-2">
          <div className="rounded-2xl border border-border bg-card p-5 sticky top-24" data-testid="contact-form">
            {done ? (
              <div className="text-center py-8" data-testid="contact-success">
                <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
                <h3 className="font-display text-xl font-semibold mt-3">Message sent!</h3>
                <p className="text-sm text-muted-foreground mt-1">Thanks for reaching out — our team will reply to your email shortly.</p>
                <Button variant="outline" className="mt-4 rounded-xl" onClick={() => setDone(false)} data-testid="contact-another">Send another</Button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-1"><Mail className="h-5 w-5 text-accent" /><h2 className="font-display text-lg font-semibold">Still need help? Contact us</h2></div>
                <p className="text-xs text-muted-foreground mb-4">Tell us what’s wrong and attach a screenshot — it lands straight in our inbox.</p>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11" data-testid="contact-name" /></div>
                    <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11" data-testid="contact-email" /></div>
                  </div>
                  <div><Label className="text-xs">Topic</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger className="h-11" data-testid="contact-category"><SelectValue /></SelectTrigger>
                      <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="h-11" data-testid="contact-subject" /></div>
                  <div><Label className="text-xs">Message</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} data-testid="contact-message" /></div>
                  <div>
                    <label className="inline-flex items-center gap-2 text-sm text-accent font-medium cursor-pointer" data-testid="contact-attach-label">
                      <Paperclip className="h-4 w-4" /> {file ? 'Change screenshot' : 'Attach screenshot (optional)'}
                      <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="contact-screenshot" />
                    </label>
                    {file && <span className="ml-2 inline-flex items-center gap-1 text-xs text-muted-foreground">{file.name}<button onClick={() => setFile(null)}><X className="h-3 w-3" /></button></span>}
                  </div>
                  <Button onClick={submit} disabled={submitting} className="w-full h-12 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 font-semibold" data-testid="contact-submit">
                    {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</> : 'Send message'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}
